import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail, BACKEND_URL } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Download, FileText, Image as ImageIcon, Sparkles, Gavel, Layers, BookOpen, Building2,
} from "lucide-react";

const resolveImg = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const KINDS = [
  { id: "site", label: "Platform", icon: Building2, hint: "Brand pitch flyer", public: true },
  { id: "artwork", label: "Artwork", icon: ImageIcon, hint: "Promote one artwork" },
  { id: "auction", label: "Auction lot", icon: Gavel, hint: "Single live lot" },
  { id: "roundup", label: "Auction roundup", icon: Layers, hint: "Multi-lot weekly" },
  { id: "story", label: "African Story", icon: BookOpen, hint: "Editorial article" },
];

const THEMES = [
  { id: "editorial", label: "Editorial", swatch: ["#F4EFE6", "#1E3A8A", "#F4C430"], hint: "Cream paper · serif headlines" },
  { id: "bold", label: "Bold", swatch: ["#0F1320", "#F4C430", "#1E3A8A"], hint: "Dark poster · gold accent" },
  { id: "tabloid", label: "Tabloid", swatch: ["#FFFFFF", "#111111", "#D32F2F"], hint: "Heavy newspaper banner" },
];

// Output presets per flyer kind. PDF + 1080² square + 1080×1920 portrait — per spec.
const FORMATS = [
  { id: "pdf", label: "PDF · A4 print", ext: "pdf" },
  { id: "square", label: "JPG · Square (1080×1080)", fmt: "jpg", size: "square", ext: "jpg" },
  { id: "story", label: "JPG · Portrait (1080×1920)", fmt: "jpg", size: "story", ext: "jpg" },
];

export default function AdminFlyer() {
  const [kind, setKind] = useState("artwork");
  const [theme, setTheme] = useState("editorial");
  const [format, setFormat] = useState("pdf");
  const [message, setMessage] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Picker state — varies by kind
  const [artworks, setArtworks] = useState([]);
  const [articles, setArticles] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");

  const token = typeof window !== "undefined" ? localStorage.getItem("aam_token") : null;

  useEffect(() => {
    api
      .get("/admin/artworks", { params: { limit: 200 } })
      .then(({ data }) => setArtworks(data.items || []))
      .catch(() => {});
    api
      .get("/admin/content", { params: { type: "post", limit: 100 } })
      .then(({ data }) => setArticles(data.items || []))
      .catch(() => {});
    api
      .get("/auctions", { params: { status: "live" } })
      .then(({ data }) => setAuctions(Array.isArray(data) ? data : data.items || []))
      .catch(() => {});
  }, []);

  // Reset selection + message whenever the kind changes.
  useEffect(() => {
    setSelectedId("");
    setSearch("");
    setMessage("");
    // Site flyer only supports PDF (backend ignores fmt) — force it to keep the
    // downloaded filename and bytes consistent.
    if (kind === "site") setFormat("pdf");
  }, [kind]);

  const needsSelection = kind === "artwork" || kind === "auction" || kind === "story";
  const items = useMemo(() => {
    if (kind === "artwork") return artworks;
    if (kind === "story") return articles;
    if (kind === "auction") return auctions;
    return [];
  }, [kind, artworks, articles, auctions]);

  const filtered = items.filter((i) => {
    const hay = `${i.title || ""} ${i.artist_name || ""} ${i.author_name || ""}`.toLowerCase();
    return !search || hay.includes(search.toLowerCase());
  });

  const selectedItem = items.find((i) => i.id === selectedId);

  const callAiCaption = async () => {
    if (kind === "site") {
      toast.info("Platform flyer uses a fixed brand pitch — message field is unused.");
      return;
    }
    if (needsSelection && !selectedId) {
      toast.error("Pick an item first");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/flyer/ai-caption", {
        target_kind:
          kind === "story" ? "article" : kind === "roundup" ? "auction_roundup" : kind,
        target_id: needsSelection ? selectedId : undefined,
        theme,
      });
      setMessage(data.message || "");
      toast.success("AI draft ready · edit as needed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI caption failed");
    } finally {
      setAiLoading(false);
    }
  };

  const downloadFlyer = async () => {
    if (kind !== "site" && kind !== "roundup" && !selectedId) {
      toast.error("Pick an item first");
      return;
    }
    setDownloading(true);
    try {
      let path;
      const preset = FORMATS.find((f) => f.id === format) || FORMATS[0];
      const params = new URLSearchParams();
      params.set("theme", theme);
      params.set("fmt", preset.id === "pdf" ? "pdf" : preset.fmt);
      if (preset.id !== "pdf") params.set("size", preset.size);
      if (message.trim()) params.set("message", message.trim());

      if (kind === "site") {
        path = `/api/flyer/site`;
        params.delete("theme");
        params.delete("size");
      } else if (kind === "artwork") {
        path = preset.id === "pdf"
          ? `/api/flyer/artwork/${selectedId}`
          : `/api/flyer/artwork/${selectedId}/image`;
      } else if (kind === "auction") {
        path = `/api/flyer/auction/${selectedId}`;
      } else if (kind === "roundup") {
        path = `/api/flyer/auctions/roundup`;
      } else if (kind === "story") {
        path = `/api/flyer/article/${selectedId}`;
      }

      const url = `${BACKEND_URL}${path}?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      const stem = selectedItem?.title || kind;
      const safe = stem.replace(/[^a-z0-9-_]+/gi, "-").slice(0, 40);
      a.download = `aam-${kind}-${safe}-${theme}.${preset.ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
      toast.success(`Flyer downloaded · ${preset.ext.toUpperCase()}`);
    } catch (err) {
      toast.error(err.message || "Could not generate flyer");
    } finally {
      setDownloading(false);
    }
  };

  const canGenerate =
    kind === "site" || kind === "roundup" || !!selectedId;

  return (
    <div data-testid="admin-flyer" className="pb-16">
      <p className="label-eyebrow text-[#1E3A8A]">— Marketing</p>
      <h1 className="font-serif-display text-5xl tracking-tighter font-medium mt-3 text-[#111]">
        Flyer studio
      </h1>
      <p className="text-[#555] mt-3 max-w-2xl">
        Generate creative flyers — for the platform, individual artworks, live auctions, weekly
        roundups, or editorial pieces from African Stories. Pick a theme, add a custom message
        (or let AI draft one), and export print-ready PDF or social-ready JPG.
      </p>

      {/* Flyer kind tabs */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="flyer-kind-tabs">
        {KINDS.map((k) => {
          const Icon = k.icon;
          const active = kind === k.id;
          return (
            <button
              key={k.id}
              type="button"
              onClick={() => setKind(k.id)}
              data-testid={`flyer-kind-${k.id}`}
              className={`text-left border p-4 transition-colors ${
                active
                  ? "border-[#1E3A8A] bg-[#1E3A8A] text-white"
                  : "border-[#E5E0D8] bg-white hover:bg-[#F4EFE6]/40"
              }`}
            >
              <Icon className={`w-5 h-5 ${active ? "text-[#F4C430]" : "text-[#1E3A8A]"}`} strokeWidth={1.5} />
              <p className={`font-serif-display text-lg mt-3 ${active ? "text-white" : "text-[#111]"}`}>
                {k.label}
              </p>
              <p className={`text-xs mt-1 ${active ? "text-white/70" : "text-[#555]"}`}>{k.hint}</p>
            </button>
          );
        })}
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Left column — picker */}
        <div>
          {kind === "site" && (
            <div className="border border-[#E5E0D8] bg-white p-6">
              <p className="label-eyebrow text-[#1E3A8A]">— Platform flyer</p>
              <p className="font-serif-display text-2xl text-[#111] mt-2">
                Brand pitch · A4 promotional
              </p>
              <p className="text-sm text-[#555] mt-3">
                Quick takeaway flyer with the brand stats, QR code, and signup CTA. Public — anyone
                can download it. Theme + message do not apply.
              </p>
            </div>
          )}
          {kind === "roundup" && (
            <div className="border border-[#E5E0D8] bg-white p-6">
              <p className="label-eyebrow text-[#1E3A8A]">— Auction roundup</p>
              <p className="font-serif-display text-2xl text-[#111] mt-2">
                Top {Math.min(auctions.length, 6)} live lots
              </p>
              <ul className="mt-4 space-y-2 text-sm text-[#555] max-h-64 overflow-y-auto">
                {auctions.slice(0, 6).map((a, i) => (
                  <li key={a.id} className="flex items-center gap-2" data-testid={`roundup-preview-${i}`}>
                    <span className="font-mono-aam text-[#1E3A8A] text-xs w-8">
                      #{String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-serif-display truncate">{a.artwork_title || a.title || a.artwork_id}</span>
                    <span className="ml-auto font-mono-aam text-xs text-[#111]">
                      ${a.current_bid?.toLocaleString() || a.starting_price?.toLocaleString()}
                    </span>
                  </li>
                ))}
                {auctions.length === 0 && (
                  <li className="text-[#A87900]" data-testid="roundup-empty">
                    No live auctions right now — start one to populate this flyer.
                  </li>
                )}
              </ul>
            </div>
          )}
          {needsSelection && (
            <>
              <p className="label-eyebrow text-[#1E3A8A]">— Pick {kind}</p>
              <Input
                data-testid="flyer-search"
                placeholder={`Search ${kind}s…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mt-3 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
              />
              <div className="mt-4 max-h-[440px] overflow-y-auto border border-[#E5E0D8] bg-white">
                {filtered.length === 0 ? (
                  <p className="p-6 label-eyebrow text-[#555]" data-testid="flyer-picker-empty">
                    No {kind}s to pick.
                  </p>
                ) : (
                  filtered.map((item) => {
                    const cover = item.image_url || item.cover_image;
                    const subtitle =
                      kind === "artwork"
                        ? `$${item.price?.toLocaleString()} · ${item.artist_name || ""}`
                        : kind === "auction"
                          ? `Starting $${item.starting_price?.toLocaleString()} · ${item.status}`
                          : `${item.status || "draft"} · ${item.author_name || ""}`;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        data-testid={`flyer-pick-${item.id}`}
                        onClick={() => setSelectedId(item.id)}
                        className={`w-full flex items-center gap-3 p-3 text-left border-b border-[#E5E0D8] last:border-b-0 hover:bg-[#F4EFE6]/40 transition-colors ${
                          selectedId === item.id ? "bg-[#F4EFE6]/70" : ""
                        }`}
                      >
                        {cover ? (
                          <img
                            src={resolveImg(cover)}
                            alt=""
                            className="w-12 h-12 object-cover border border-[#E5E0D8] flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-[#F4EFE6] border border-[#E5E0D8] flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-serif-display text-base text-[#111] truncate">
                            {item.title || "Untitled"}
                          </p>
                          <p className="font-mono-aam text-xs text-[#555] mt-0.5 truncate">
                            {subtitle}
                          </p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* Right column — theme/format/message/AI */}
        <div className="space-y-6">
          {kind !== "site" && (
            <>
              <div>
                <p className="label-eyebrow text-[#1E3A8A]">— Theme</p>
                <div className="mt-3 grid grid-cols-3 gap-3" data-testid="flyer-theme-picker">
                  {THEMES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`flyer-theme-${t.id}`}
                      onClick={() => setTheme(t.id)}
                      className={`border p-3 text-left transition-colors ${
                        theme === t.id
                          ? "border-[#1E3A8A] bg-[#F4EFE6]/70"
                          : "border-[#E5E0D8] bg-white hover:bg-[#F4EFE6]/40"
                      }`}
                    >
                      <div className="flex gap-1.5">
                        {t.swatch.map((s) => (
                          <span
                            key={s}
                            style={{ background: s }}
                            className="w-5 h-5 inline-block border border-[#E5E0D8]"
                          />
                        ))}
                      </div>
                      <p className="font-mono-aam text-xs uppercase mt-2 text-[#111]">
                        {t.label}
                      </p>
                      <p className="text-[10px] text-[#555] mt-0.5 leading-tight">{t.hint}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <p className="label-eyebrow text-[#1E3A8A]">— Marketing message</p>
                  <button
                    type="button"
                    data-testid="flyer-ai-draft"
                    onClick={callAiCaption}
                    disabled={aiLoading || (needsSelection && !selectedId)}
                    className="label-eyebrow text-[#1E3A8A] hover:text-[#111] disabled:opacity-40 flex items-center gap-1"
                  >
                    <Sparkles className="w-3 h-3" />
                    {aiLoading ? "Drafting…" : "AI draft"}
                  </button>
                </div>
                <Textarea
                  data-testid="flyer-message"
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    kind === "auction"
                      ? "e.g. 'A rare lot from our July auction — bidding closes Friday.'"
                      : kind === "story"
                        ? "e.g. 'Read our new long-form on Lagos studio practice.'"
                        : "e.g. 'A curated selection of summer drops — limited edition.'"
                  }
                  className="mt-3 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
                />
                <p className="text-xs text-[#555] mt-2">
                  Up to ~240 characters fit cleanly. Leave blank for a no-message layout.
                </p>
              </div>
            </>
          )}

          <div>
            <p className="label-eyebrow text-[#1E3A8A]">— Output format</p>
            <div className="mt-3 space-y-2" data-testid="flyer-format-picker">
              {(kind === "site" ? FORMATS.slice(0, 1) : FORMATS).map((f) => (
                <label
                  key={f.id}
                  data-testid={`flyer-format-${f.id}`}
                  className={`flex items-start gap-3 border p-3 cursor-pointer transition-colors ${
                    format === f.id
                      ? "border-[#1E3A8A] bg-[#F4EFE6]/70"
                      : "border-[#E5E0D8] bg-white hover:bg-[#F4EFE6]/40"
                  }`}
                >
                  <input
                    type="radio"
                    name="flyer-format"
                    value={f.id}
                    checked={format === f.id}
                    onChange={() => setFormat(f.id)}
                    className="mt-1 accent-[#1E3A8A]"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono-aam text-xs uppercase tracking-wider text-[#111]">
                      {f.ext}
                    </p>
                    <p className="text-sm text-[#555] mt-0.5">{f.label}</p>
                  </div>
                  {f.id === "pdf" ? (
                    <FileText className="w-4 h-4 text-[#1E3A8A] flex-shrink-0" strokeWidth={1.5} />
                  ) : (
                    <ImageIcon className="w-4 h-4 text-[#1E3A8A] flex-shrink-0" strokeWidth={1.5} />
                  )}
                </label>
              ))}
            </div>
          </div>

          <Button
            onClick={downloadFlyer}
            disabled={!canGenerate || downloading}
            data-testid="flyer-download-btn"
            className="w-full rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white py-5 h-auto label-eyebrow disabled:opacity-40"
          >
            <Download className="w-3.5 h-3.5 mr-2" />
            {downloading ? "Generating…" : `Download ${(FORMATS.find((f) => f.id === format) || FORMATS[0]).ext.toUpperCase()}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
